// Terpene profile data for Polish medical cannabis strains.
// The catalogue is split per producer under src/lib/strains/ — add or edit a
// strain in its producer file, this module only composes and exposes them.
import { CANOPY_STRAINS } from "./strains/canopy.js";
import { AURORA_STRAINS } from "./strains/aurora.js";
import { SLAB_STRAINS } from "./strains/slab.js";
import { TILRAY_STRAINS } from "./strains/tilray.js";
import { COSMA_STRAINS } from "./strains/cosma.js";
import { FOUR20_STRAINS } from "./strains/four20.js";
import { PHYTOPUR_STRAINS } from "./strains/phytopur.js";
import { SUPROBION_STRAINS } from "./strains/suprobion.js";
import { SYNOPTIS_STRAINS } from "./strains/synoptis.js";
import { CANTOURAGE_STRAINS } from "./strains/cantourage.js";
import { BLISS_STRAINS } from "./strains/bliss.js";
import { MEDALCHEMY_STRAINS } from "./strains/medalchemy.js";

export const TERPENES_LIST = [
  "mircen", "limonen", "kariofilen", "terpinolen", "pinen",
  "linalol", "humulen", "ocymen", "bisabolol", "nerolidol",
  "guaiol", "farnezen", "selinadien", "cedren", "walenzen", "bulnesol"
];

export const STRAINS = [
  ...CANOPY_STRAINS,
  ...AURORA_STRAINS,
  ...SLAB_STRAINS,
  ...TILRAY_STRAINS,
  ...COSMA_STRAINS,
  ...FOUR20_STRAINS,
  ...PHYTOPUR_STRAINS,
  ...SUPROBION_STRAINS,
  ...SYNOPTIS_STRAINS,
  ...CANTOURAGE_STRAINS,
  ...BLISS_STRAINS,
  ...MEDALCHEMY_STRAINS,
];

// Derived so the displayed catalogue size can never drift from the real data.
export const STRAINS_COUNT = STRAINS.length;

export function getCbdDisplay(strain) {
  if (strain.cbd > 1) return `${strain.cbd}%`;
  if (strain.cbd === 1) return `≤1%`;
  if (strain.cbd === 0.4) return `<0,5%`;
  return `<1%`;
}

export function getSimilarStrains(strainId, limit = 6) {
  const strain = STRAINS.find(s => s.id === strainId);
  if (!strain) return [];

  const scored = STRAINS
    .filter(s => s.id !== strainId)
    .map(s => {
      let score = 0;
      TERPENES_LIST.forEach(t => {
        const a = strain.terpenes[t] || 0;
        const b = s.terpenes[t] || 0;
        score += Math.abs(a - b);
      });
      return { strain: s, score };
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map(x => x.strain);

  return scored;
}

// Same ranking as getSimilarStrains, but also returns a 0-100 terpene-profile
// similarity percentage for display (e.g. "92% podobieństwa profilu").
export function getSimilarStrainsWithSimilarity(strainId, limit = 6) {
  const strain = STRAINS.find(s => s.id === strainId);
  if (!strain) return [];

  return STRAINS
    .filter(s => s.id !== strainId)
    .map(s => {
      let score = 0;
      TERPENES_LIST.forEach(t => {
        const a = strain.terpenes[t] || 0;
        const b = s.terpenes[t] || 0;
        score += Math.abs(a - b);
      });
      // Terpene percentages per strain sum to ~100, so the max possible
      // total absolute difference is ~200 (fully disjoint profiles).
      const similarity = Math.max(0, Math.round(100 - score / 2));
      return { strain: s, similarity };
    })
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, limit);
}

// ── SEO slug helpers ──────────────────────────────────────────────────────
export function strainSlug(strain) {
  const gen = strain.genetics.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${strain.id}-thc${strain.thc}-${gen}`;
}

export function findStrainBySlug(slug) {
  return STRAINS.find(s => s.id === slug || strainSlug(s) === slug);
}

export function producerSlug(producer) {
  return producer.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function findProducerBySlug(slug) {
  const producers = [...new Set(STRAINS.map(s => s.producer))];
  return producers.find(p => producerSlug(p) === slug);
}